import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { updateVisit } from "../../api/reportsApi";
import { splitVisitName, validateVisit } from "../../utils/visitName";

// Tytuł raportu z wizyty: typ wizyty i numer edytowane osobno, nazwa = typ + numer (jedno źródło numeru, 04.11).
// Ten sam tekst co na liście raportów i w tytule PDF.
type Props = {
  reportId: string;
  studyId?: string;
  name: string;
  visitName?: string | null;
  visitNumber?: number | null;
};

const fieldCls =
  "rounded-lg border bg-[var(--sc-surface)] px-3 py-1.5 text-[15px] font-semibold text-[var(--sc-text)] outline-none focus:ring-1 focus:ring-[var(--sc-amber)]";

export const VisitTitleEditor = ({ reportId, studyId, name, visitName, visitNumber }: Props) => {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [type, setType] = useState("");
  const [numberText, setNumberText] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);

  const error = isEditing ? validateVisit(type, numberText) : null;

  const { mutate: save, isLoading } = useMutation(
    () => updateVisit(reportId, type.trim(), numberText.trim() === "" ? null : Number(numberText)),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries([reportId, "Report"]);
        queryClient.invalidateQueries(["Studies"]);
        if (studyId) queryClient.invalidateQueries([studyId, "Study"]);
        setIsEditing(false);
        if (res?.duplicates?.length) {
          toast.warn(`W tym ośrodku jest już raport z tym numerem: ${res.duplicates.join(", ")}.`, { autoClose: 8000 });
        }
      },
      onError: (e: any) => {
        const msg = typeof e?.response?.data === "string" ? e.response.data : "Nie udało się zapisać typu i numeru wizyty.";
        setServerError(msg);
      },
    }
  );

  const start = () => {
    // Raport bez zapisanego typu wizyty: typ i numer z nazwy
    const fromName = splitVisitName(name);
    setType(visitName ?? fromName.type);
    setNumberText(String((visitName != null ? visitNumber : fromName.number) ?? ""));
    setServerError(null);
    setIsEditing(true);
  };

  const submit = () => {
    if (error || isLoading) return;
    setServerError(null);
    save();
  };

  if (!isEditing) {
    return (
      <h1
        className="text-[18px] font-bold text-[var(--sc-text)] cursor-text hover:text-[var(--sc-muted)] transition-colors group flex items-center gap-2"
        onClick={start}
        title="Kliknij, aby zmienić typ lub numer wizyty"
      >
        {name}
        <span className="text-[12px] font-normal text-[var(--sc-muted)] opacity-0 group-hover:opacity-100 transition-opacity">✎</span>
      </h1>
    );
  }

  const shownError = error ?? serverError;
  const typeInvalid = !!error && !error.startsWith("Numer");
  const numberInvalid = !!error && error.startsWith("Numer");

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") setIsEditing(false);
      }}
    >
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-medium uppercase tracking-[0.05em] text-[var(--sc-muted)]">Typ wizyty</span>
          <input
            autoFocus
            value={type}
            onChange={(e) => setType(e.target.value)}
            aria-invalid={typeInvalid}
            className={`${fieldCls} w-[260px] ${typeInvalid ? "border-[#C03030]" : "border-[var(--sc-border)]"}`}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-medium uppercase tracking-[0.05em] text-[var(--sc-muted)]">Numer</span>
          <input
            inputMode="numeric"
            value={numberText}
            onChange={(e) => setNumberText(e.target.value.replace(/\D/g, ""))}
            aria-invalid={numberInvalid}
            className={`${fieldCls} w-[90px] ${numberInvalid ? "border-[#C03030]" : "border-[var(--sc-border)]"}`}
          />
        </label>
        <button
          type="submit"
          disabled={!!error || isLoading}
          className="rounded-lg bg-[#F4A832] px-4 py-2 text-[13px] font-semibold text-[#0F0F0F] hover:bg-[#e09920] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading ? "Zapisywanie…" : "Zapisz"}
        </button>
        <button
          type="button"
          onClick={() => setIsEditing(false)}
          className="rounded-lg px-3 py-2 text-[13px] text-[var(--sc-muted)] hover:text-[var(--sc-text)]"
        >
          Anuluj
        </button>
      </div>
      {shownError ? (
        <p role="alert" className="mt-2 text-[12px] text-[#C03030]">{shownError}</p>
      ) : (
        <p className="mt-2 text-[12px] text-[var(--sc-muted)]">
          Nazwa raportu: <span className="font-medium text-[var(--sc-text)]">{[type.trim(), numberText.trim()].filter(Boolean).join(" ") || "—"}</span>
        </p>
      )}
    </form>
  );
};
